import { useState, useEffect } from 'react';
import { 
  Search, 
  Mail, 
  CheckCircle, 
  Clock, 
  FileText,
  Send,
  Calendar,
  User,
  Phone,
  Package,
  Eye,
  X,
  EyeOff
} from 'lucide-react';
import { useConfirmaciones, useOcultarConfirmacion } from '../../../hooks/useConfirmaciones';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { formatDate } from '../../../lib/utils';
import { EnviarEmailModal } from './EnviarEmailModal';

interface ConfirmacionListProps {
  envioId?: number;
}

export function ConfirmacionList({ envioId }: ConfirmacionListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntrega, setSelectedEntrega] = useState<any>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [mostrarOcultos, setMostrarOcultos] = useState(false);

  const { data: entregas, isLoading, refetch } = useConfirmaciones({ 
    incluirOcultos: mostrarOcultos 
  });
  const ocultarConfirmacion = useOcultarConfirmacion();

  useEffect(() => {
    if (envioId && entregas) {
      const entrega = entregas.find((e: any) => e.id === envioId);
      if (entrega) {
        setSelectedEntrega(entrega);
        setShowEmailModal(true);
      }
    }
  }, [envioId, entregas]);

  const filteredEntregas = entregas?.filter((e: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      e.numeroEnvio?.toLowerCase().includes(searchLower) ||
      e.clienteNombre?.toLowerCase().includes(searchLower) ||
      e.clienteTelefono?.toLowerCase().includes(searchLower)
    );
  }) || [];

  const handleEnviarEmail = (entrega: any) => {
    setSelectedEntrega(entrega);
    setShowEmailModal(true);
  };

  const handleVerDetalle = (entrega: any) => {
    setSelectedEntrega(entrega);
    setShowDetailModal(true);
  };

  const handleOcultar = async (id: number, numeroEnvio: string) => {
    if (!confirm(`¿Ocultar la tarjeta de ${numeroEnvio}? Podrás ver el email en Gmail.`)) return;
    
    try {
      await ocultarConfirmacion.mutateAsync(id);
      refetch();
    } catch (error) {
      // error ya manejado por react-query
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando entregas confirmadas...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header con estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-full">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">{entregas?.length || 0}</p>
              <p className="text-sm text-gray-500">Entregas completadas</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <Mail size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">
                {entregas?.filter((e: any) => e.emailEnviado).length || 0}
              </p>
              <p className="text-sm text-gray-500">Emails enviados</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-full">
              <Clock size={20} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-800">
                {entregas?.filter((e: any) => !e.emailEnviado).length || 0}
              </p>
              <p className="text-sm text-gray-500">Pendientes de email</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Controles */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por número de envío, cliente o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
        
        <button
          onClick={() => setMostrarOcultos(!mostrarOcultos)}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
            mostrarOcultos 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <EyeOff size={16} />
          {mostrarOcultos ? 'Mostrando ocultos' : 'Ver ocultos'}
        </button>
      </div>

      {/* Lista de entregas */}
      <div className="space-y-4">
        {filteredEntregas.map((entrega: any) => {
          const productos = typeof entrega.productos === 'string'
            ? JSON.parse(entrega.productos)
            : entrega.productos || [];

          return (
            <Card key={entrega.id} className={`p-4 hover:shadow-md transition-shadow ${entrega.oculto ? 'opacity-50 bg-gray-50' : ''}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Info principal */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-semibold text-gray-800">{entrega.numeroEnvio}</h3>
                    <Badge variant="success">Entregado</Badge>
                    {entrega.oculto && (
                      <Badge variant="default" className="bg-gray-500 text-white">Oculto</Badge>
                    )}
                    {entrega.emailEnviado ? (
                      <Badge variant="info" className="bg-blue-100 text-blue-700">
                        <Mail size={12} className="mr-1" />
                        Email enviado
                      </Badge>
                    ) : (
                      <Badge variant="warning" className="bg-yellow-100 text-yellow-700">
                        <Clock size={12} className="mr-1" />
                        Pendiente
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm">
                    <div className="flex items-center gap-2">
                      <User size={14} className="text-gray-400" />
                      <span className="text-gray-700">{entrega.clienteNombre}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-gray-400" />
                      {entrega.clienteEmail ? (
                        <span className="text-blue-600 text-sm">{entrega.clienteEmail}</span>
                      ) : (
                        <span className="text-yellow-600 text-sm">Sin email</span>
                      )}
                    </div>
                    {entrega.clienteTelefono && (
                      <div className="flex items-center gap-2">
                        <Phone size={14} className="text-gray-400" />
                        <span className="text-gray-600">{entrega.clienteTelefono}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-gray-400" />
                      <span className="text-gray-600">
                        {formatDate(entrega.fechaEntrega)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Package size={14} className="text-gray-400" />
                      <span className="text-gray-600">
                        {productos.length} producto(s)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVerDetalle(entrega)}
                    className="text-gray-600"
                  >
                    <Eye size={16} />
                    <span className="hidden sm:inline ml-1">Ver</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleEnviarEmail(entrega)}
                    className={entrega.emailEnviado ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-600 hover:bg-green-700'}
                    disabled={!entrega.clienteEmail}
                  >
                    <Send size={16} />
                    <span className="hidden sm:inline ml-1">
                      {entrega.emailEnviado ? 'Reenviar' : 'Enviar Email'}
                    </span>
                  </Button>
                  {entrega.emailEnviado && !entrega.oculto && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOcultar(entrega.id, entrega.numeroEnvio)}
                      className="text-gray-600 border-gray-300 hover:bg-gray-50"
                    >
                      <EyeOff size={16} />
                      <span className="hidden sm:inline ml-1">Ocultar</span>
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        {filteredEntregas.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <CheckCircle size={48} className="mx-auto mb-4 text-gray-300" />
            <p>{mostrarOcultos ? 'No hay tarjetas ocultas' : 'No hay entregas confirmadas'}</p>
            <p className="text-sm mt-1">
              {mostrarOcultos 
                ? 'Las tarjetas ocultas aparecerán aquí' 
                : 'Las entregas aparecerán aquí cuando se confirmen'}
            </p>
          </div>
        )}
      </div>

      {/* Modales */}
      {showEmailModal && selectedEntrega && (
        <EnviarEmailModal
          entrega={selectedEntrega}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedEntrega(null);
          }}
          onSuccess={() => {
            refetch();
            setShowEmailModal(false);
            setSelectedEntrega(null);
          }}
        />
      )}

      {/* Modal de detalle */}
      {showDetailModal && selectedEntrega && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Detalle de Entrega</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Número de envío</p>
                  <p className="font-medium">{selectedEntrega.numeroEnvio}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Estado</p>
                  <Badge variant="success">Entregado</Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cliente</p>
                  <p className="font-medium">{selectedEntrega.clienteNombre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Teléfono</p>
                  <p className="font-medium">{selectedEntrega.clienteTelefono || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium flex items-center gap-2">
                    {selectedEntrega.clienteEmail ? (
                      <>
                        <Mail size={16} className="text-blue-500" />
                        <span className="text-blue-600">{selectedEntrega.clienteEmail}</span>
                      </>
                    ) : (
                      <span className="text-yellow-600">Sin email registrado</span>
                    )}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Dirección</p>
                  <p className="font-medium">{selectedEntrega.clienteDireccion}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fecha de entrega</p>
                  <p className="font-medium">{formatDate(selectedEntrega.fechaEntrega)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email enviado</p>
                  <p className="font-medium">
                    {selectedEntrega.emailEnviado ? ' Sí' : ' No'}
                  </p>
                </div>
              </div>

              {selectedEntrega.facturaUrl && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500 mb-2">Factura adjunta</p>
                  <a
                    href={selectedEntrega.facturaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-2"
                  >
                    <FileText size={16} />
                    Ver factura
                  </a>
                </div>
              )}

              {selectedEntrega.observaciones && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-500">Observaciones</p>
                  <p className="text-sm text-gray-700">{selectedEntrega.observaciones}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}